// src/business/pages/hr/HREmployeeDetail.jsx
// HR › Employees › one person (UI-3E). A detail workspace: who they are and their employment on the overview,
// each HR record a route-backed section that summarises and links to the workflow that owns it.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { businessApi } from "../../../services/api";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import { parseTimestamp, timeAgo } from "../../../utils/timeAgo";
import { getActivityActionText } from "../../../utils/activityLogText";
import { getPayrollPayoutStatusMeta, getPayslipStatusMeta } from "../../utils/payrollLifecycle";
import { formatDay } from "./myHrText";
import EmploymentStructureModal, { authorityLabel } from "./components/EmploymentStructureModal";
import EmployeeCompensationPanel from "./components/EmployeeCompensationPanel";
import EmployeePaySchedulePanel from "./components/EmployeePaySchedulePanel";
import GovernmentNumbersPanel from "./components/GovernmentNumbersPanel";
import HolidayPayHandlingPanel from "./components/HolidayPayHandlingPanel";
import PayRuleCoveragePanel from "./components/PayRuleCoveragePanel";
import PayRuleApplicabilityPanel from "./components/PayRuleApplicabilityPanel";
import StatutoryFactsPanel from "./components/StatutoryFactsPanel";
import { ReinstateDialog, SuspendDialog } from "./components/EmployeeLifecycleDialogs";
import { EMPLOYEE_NUMBER_LABELS } from "./statutoryView";
import {
  Button, EmptyState, ErrorState, Field, Money, Notice, PageHeader, RouteTabs, SkeletonRows, Status, Surface,
  Table, TableContainer, TBody, Td, Textarea, Th, THead, Tr, plainError,
} from "../../../components/ui";
import {
  eventWord, isEmployeeRecord, isFormerMember, isInvitation, isOwnerMember, lifecycleActions, memberStatus, roleName, statusWord, visibleSections,
} from "./employeeView";

const PRE_ACTIVATION_STAGES = new Set([
  "NOT_STARTED", "IN_PROGRESS", "AWAITING_EMPLOYEE_INPUT", "AWAITING_BUSINESS_SETUP", "READY_FOR_REVIEW",
  "SUBMITTED_FOR_REVIEW", "RESUBMITTED_FOR_REVIEW", "RETURNED_FOR_REVISION", "APPROVED_FOR_ACTIVATION",
  "AWAITING_EMPLOYEE_COMPLETION", "AWAITING_BUSINESS_COMPLETION", "UNDER_REVIEW", "SETUP_PENDING",
]);
const isPreActivation = (member) => memberStatus(member) === "ON_HOLD" || PRE_ACTIVATION_STAGES.has(String(member?.onboardingStage || "").toUpperCase());

const day = (value) => {
  if (!value) return null;
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text.slice(0, 10)) && text.length <= 10) return formatDay(text, { year: true });
  const parsed = parseTimestamp(value);
  return parsed ? parsed.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }) : null;
};
const time = (value) => (value ? new Date(value).toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" }) : "—");
const minutes = (m) => {
  if (!m) return "—";
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
};
const words = (value) => {
  const text = String(value || "").replace(/_/g, " ").trim();
  return text ? text[0].toUpperCase() + text.slice(1).toLowerCase() : "—";
};

/** Label–value facts. A fact nobody has recorded says so in words, quieter than a recorded one. */
function Facts({ items, columns = 3 }) {
  const shown = items.filter(([, value]) => value !== undefined);
  return (
    <dl className={`grid grid-cols-2 gap-x-6 gap-y-3 ${columns === 3 ? "lg:grid-cols-3" : ""}`}>
      {shown.map(([label, value, options]) => (
        // `wide`: a value that cannot be shortened (an email) takes the whole row on a phone, so it is not cut mid-word.
        <div key={label} className={`min-w-0 ${options?.wide ? "col-span-2 sm:col-span-1" : ""}`}>
          <dt className="text-meta text-secondary">{label}</dt>
          <dd className={`mt-0.5 text-body [overflow-wrap:anywhere] ${value ? "text-foreground" : "text-tertiary"}`}>{value || "Not recorded"}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * One named group of facts inside a section (DESIGN.md › Detail workspace): the section says what the record is, the
 * group says which part. A group with nothing recorded is one sentence, not a grid of blanks.
 */
function FactGroup({ title, items, columns = 3, none, hint }) {
  const shown = items.filter(([, value]) => value !== undefined);
  const empty = none && shown.every(([, value]) => !value);
  return (
    <div className="space-y-2">
      {title ? <h3 className="text-label text-secondary">{title}</h3> : null}
      {empty ? <p className="text-body-sm text-tertiary">{none}</p> : <Facts items={shown} columns={columns} />}
      {hint && !empty ? <p className="max-w-[75ch] text-meta text-tertiary">{hint}</p> : null}
    </div>
  );
}

function SectionSurface({ title, action, children, padded = true }) {
  return (
    <Surface
      as="section"
      aria-label={title}
      toolbar={(
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-panel-title text-foreground">{title}</h2>
          {action}
        </div>
      )}
    >
      <div className={padded ? "p-4" : ""}>{children}</div>
    </Surface>
  );
}

function useSectionData(load, deps) {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const run = useCallback(() => {
    let active = true;
    setState((current) => ({ ...current, loading: true, error: "" }));
    load()
      .then((data) => active && setState({ loading: false, error: "", data }))
      .catch((err) => active && setState({ loading: false, error: plainError(err, "Check your connection and try again."), data: null }));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => run(), [run]);
  return { ...state, retry: run };
}

function SectionBody({ state, columns, empty, children }) {
  if (state.loading) return <SkeletonRows rows={4} columns={columns} />;
  if (state.error) return <ErrorState title="This section did not load" description={state.error} onRetry={state.retry} />;
  if (empty) return <EmptyState title={empty.title} description={empty.description} />;
  return children;
}

/* ── Sections ─────────────────────────────────────────────────── */

function AttendanceSection({ member }) {
  const state = useSectionData(async () => {
    const res = await businessApi.getEmployeeAttendance(member.id);
    return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
  }, [member.id]);
  const rows = state.data || [];
  return (
    <SectionSurface title="Attendance" padded={false} action={<Link to="/business/hr/attendance" className="text-body-sm text-accent hover:underline">Open Attendance</Link>}>
      <SectionBody state={state} columns={6} empty={!rows.length && { title: "No attendance yet", description: "Days they clock in appear here." }}>
        <TableContainer className="relative">
          <Table caption={`${member.name}'s attendance`}>
            <THead>
              <Tr><Th>Date</Th><Th>Clock in</Th><Th>Clock out</Th><Th numeric>Paid time</Th><Th numeric>Late</Th><Th numeric>Overtime (approved)</Th><Th>Status</Th></Tr>
            </THead>
            <TBody>
              {rows.map((r) => (
                <Tr key={r.id}>
                  <Td className="whitespace-nowrap">{day(String(r.date).slice(0, 10))}</Td>
                  <Td>{time(r.check_in)}</Td>
                  <Td>{time(r.check_out)}</Td>
                  <Td numeric>{minutes(r.worked_minutes)}</Td>
                  <Td numeric>{minutes(r.classification?.lateMinutes ?? r.late_minutes)}</Td>
                  <Td numeric>{r.overtime_minutes || r.approved_overtime_minutes ? `${minutes(r.overtime_minutes || 0)} (${minutes(r.approved_overtime_minutes || 0)})` : "—"}</Td>
                  <Td>{words(r.status)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      </SectionBody>
    </SectionSurface>
  );
}

function LeaveSection({ member }) {
  const state = useSectionData(async () => {
    const [balances, requests] = await Promise.all([
      businessApi.getLeaveBalances(member.id),
      businessApi.getLeaveRequests({ memberId: member.id, limit: 50 }),
    ]);
    return { balances: balances?.balances || [], requests: requests?.rows || [] };
  }, [member.id]);
  const data = state.data || { balances: [], requests: [] };
  return (
    <div className="space-y-6">
      <SectionSurface title="Leave balances" padded={false} action={<Link to="/business/hr/leave" className="text-body-sm text-accent hover:underline">Open Leave</Link>}>
        <SectionBody state={state} columns={4} empty={!data.balances.length && { title: "No leave balances", description: "Balances appear once leave types are set up for them." }}>
          <TableContainer className="relative">
            <Table caption="Leave balances">
              <THead><Tr><Th>Leave type</Th><Th numeric>Entitled</Th><Th numeric>Used</Th><Th numeric>Left</Th></Tr></THead>
              <TBody>
                {data.balances.map((b) => (
                  <Tr key={b.id}>
                    <Td>{b.leave_type_name || "Leave"}</Td>
                    <Td numeric>{b.entitled ?? 0}</Td>
                    <Td numeric>{b.used ?? 0}</Td>
                    <Td numeric className="font-medium">{b.balance ?? 0}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </SectionBody>
      </SectionSurface>
      <SectionSurface title="Leave requests" padded={false}>
        <SectionBody state={state} columns={4} empty={!data.requests.length && { title: "No leave requests", description: "Requests they make appear here." }}>
          <TableContainer className="relative">
            <Table caption="Leave requests">
              <THead><Tr><Th>Leave type</Th><Th>From</Th><Th>To</Th><Th numeric>Days</Th><Th>Status</Th></Tr></THead>
              <TBody>
                {data.requests.map((r) => (
                  <Tr key={r.id}>
                    <Td>{r.leave_type_name || "Leave"}</Td>
                    <Td className="whitespace-nowrap">{day(String(r.start_date).slice(0, 10))}</Td>
                    <Td className="whitespace-nowrap">{day(String(r.end_date).slice(0, 10))}</Td>
                    <Td numeric>{r.total_days ?? "—"}</Td>
                    <Td>{words(r.status)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </SectionBody>
      </SectionSurface>
    </div>
  );
}

function DeductionsSection({ member }) {
  const state = useSectionData(async () => (await businessApi.getEmployeeDeductions(member.id))?.deductions || [], [member.id]);
  const rows = state.data || [];
  return (
    <SectionSurface title="Deductions" padded={false} action={<Link to="/business/hr/deductions/employees" className="text-body-sm text-accent hover:underline">Open Deductions</Link>}>
      <SectionBody state={state} columns={4} empty={!rows.length && { title: "No deductions", description: "Deductions assigned to them in HR › Deductions appear here." }}>
        <TableContainer className="relative">
          <Table caption="Deductions">
            <THead><Tr><Th>Deduction</Th><Th numeric>Amount</Th><Th>Applies</Th><Th>Status</Th></Tr></THead>
            <TBody>
              {rows.map((d) => (
                <Tr key={d.id}>
                  <Td>{d.deduction_name || "Deduction"}</Td>
                  <Td numeric><Money value={d.amount_cents} /></Td>
                  <Td className="whitespace-nowrap">{day(String(d.effective_from).slice(0, 10)) || "—"} to {d.effective_until ? day(String(d.effective_until).slice(0, 10)) : "now"}</Td>
                  <Td>{d.is_active ? "Active" : "Ended"}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      </SectionBody>
    </SectionSurface>
  );
}

function PayslipsSection({ member }) {
  const state = useSectionData(async () => (await businessApi.getMemberPayslips(member.id, { limit: 100 }))?.rows || [], [member.id]);
  const rows = state.data || [];
  return (
    <SectionSurface title="Payslips" padded={false} action={<Link to="/business/hr/payroll" className="text-body-sm text-accent hover:underline">Open Payroll</Link>}>
      <SectionBody state={state} columns={6} empty={!rows.length && { title: "No payslips yet", description: "Payslips appear here once payroll is calculated for them." }}>
        <TableContainer className="relative">
          <Table caption="Payslips">
            <THead><Tr><Th>Period</Th><Th numeric>Gross</Th><Th numeric>Deductions</Th><Th numeric>Net pay</Th><Th>Payslip</Th><Th>Salary payment</Th></Tr></THead>
            <TBody>
              {rows.map((ps) => {
                const slip = getPayslipStatusMeta(ps.payslip_storage_status || ps.status);
                const payout = getPayrollPayoutStatusMeta({ payslipStatus: ps.payslip_storage_status || ps.status, financialRecordStatus: ps.payout_record_status });
                return (
                  <Tr key={ps.id} className={slip.status === "voided" ? "text-tertiary" : undefined}>
                    <Td>
                      <Link to={`/business/hr/payroll/payslips/${ps.id}`} className="rounded-control font-medium text-accent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus">
                        {ps.period_name || `${day(ps.period_start_date)} – ${day(ps.period_end_date)}`}
                      </Link>
                    </Td>
                    <Td numeric><Money value={ps.gross_pay_cents} /></Td>
                    <Td numeric><Money value={ps.total_deductions_cents} /></Td>
                    <Td numeric className="font-medium"><Money value={ps.net_pay_cents} /></Td>
                    <Td>{slip.label}</Td>
                    <Td>{payout.label}</Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </TableContainer>
      </SectionBody>
    </SectionSurface>
  );
}

function FinancialsSection({ member }) {
  const state = useSectionData(async () => {
    const res = await businessApi.getMemberFinancialRecords(member.id, { limit: 100 });
    return { summary: res?.summary || null, records: res?.records || [] };
  }, [member.id]);
  const data = state.data || { summary: null, records: [] };
  const s = data.summary || {};
  const figures = [
    ["Still owed to them", s.open_payable_cents],
    ["Still owed by them", s.open_receivable_cents],
    ["Payroll still open", s.payroll_open_cents],
    ["Settled", s.settled_total_cents],
  ];
  return (
    <div className="space-y-6">
      {!state.loading && !state.error && data.summary ? (
        <Surface as="section" aria-label="Balances with this employee" padded>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
            {figures.map(([label, value]) => (
              <div key={label}>
                <dt className="text-meta text-secondary">{label}</dt>
                <dd className="text-metric tabular-nums text-foreground"><Money value={value || 0} /></dd>
              </div>
            ))}
          </dl>
        </Surface>
      ) : null}
      <SectionSurface title="Financial records" padded={false}>
        <SectionBody state={state} columns={5} empty={!data.records.length && { title: "No financial records", description: "Salary payments, loans and advances with this employee appear here." }}>
          <TableContainer className="relative">
            <Table caption="Financial records">
              <THead><Tr><Th>Recorded</Th><Th>Record</Th><Th>Direction</Th><Th numeric>Amount</Th><Th numeric>Balance</Th><Th>Status</Th></Tr></THead>
              <TBody>
                {data.records.map((record) => (
                  <Tr key={record.id}>
                    <Td className="whitespace-nowrap">{day(record.recognized_on) || "—"}</Td>
                    <Td>
                      <div>{record.title}</div>
                      <div className="text-meta text-tertiary">{[words(record.category), record.payroll_period_name].filter((v) => v && v !== "—").join(" · ")}</div>
                    </Td>
                    <Td>{String(record.direction).toLowerCase() === "receivable" ? "Owed by them" : "Owed to them"}</Td>
                    <Td numeric><Money value={record.net_amount_cents} /></Td>
                    <Td numeric><Money value={record.balance_cents} /></Td>
                    <Td>{words(record.status)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </SectionBody>
      </SectionSurface>
    </div>
  );
}

function HistorySection({ member }) {
  const state = useSectionData(async () => {
    const res = await businessApi.getEmploymentHistory({ memberId: member.id });
    return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
  }, [member.id]);
  const rows = state.data || [];
  return (
    <SectionSurface title="Employment history" padded={false}>
      <SectionBody state={state} columns={4} empty={!rows.length && { title: "No employment history", description: "Hiring, suspensions and separations are recorded here." }}>
        <TableContainer className="relative">
          <Table caption="Employment history">
            <THead><Tr><Th>Date</Th><Th>What happened</Th><Th>Reason</Th><Th>By</Th></Tr></THead>
            <TBody>
              {rows.map((h) => (
                <Tr key={h.id}>
                  <Td className="whitespace-nowrap">{day(h.eventDate || h.createdAt) || "—"}</Td>
                  <Td>{eventWord(h.eventType)}</Td>
                  <Td>
                    <div>{h.reason || "—"}</div>
                    {h.remarks ? <div className="text-meta text-tertiary">{h.remarks}</div> : null}
                  </Td>
                  <Td>{h.actorName || "—"}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      </SectionBody>
    </SectionSurface>
  );
}

function ActivitySection({ member }) {
  const state = useSectionData(async () => {
    const res = await businessApi.getMemberActivity(member.id);
    return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
  }, [member.id]);
  const rows = state.data || [];
  return (
    <SectionSurface title="Activity" padded={false}>
      <SectionBody state={state} columns={2} empty={!rows.length && { title: "No activity", description: "Changes made by or to this employee appear here." }}>
        <ul className="divide-y divide-subtle">
          {rows.map((log) => (
            <li key={log.id} className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
              <div className="min-w-0">
                <div className="text-body text-foreground">{getActivityActionText(log)}</div>
                {log.description ? <div className="text-body-sm text-secondary">{log.description}</div> : null}
              </div>
              <div className="shrink-0 text-meta text-tertiary">{timeAgo(log.createdAt || log.created_at)}</div>
            </li>
          ))}
        </ul>
      </SectionBody>
    </SectionSurface>
  );
}

function NotesSurface({ member, canEdit, onSaved }) {
  const [notes, setNotes] = useState(member.remarks || "");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { setNotes(member.remarks || ""); setResult(null); }, [member.id, member.remarks]);
  const dirty = notes.trim() !== String(member.remarks || "").trim();
  const save = async () => {
    setSaving(true);
    setResult(null);
    try {
      await businessApi.updateMemberNotes(member.id, notes.trim());
      setResult({ tone: "success", text: "Notes saved." });
      onSaved?.();
    } catch (err) {
      setResult({ tone: "danger", text: plainError(err, "The notes were not saved. Try again.") });
    } finally {
      setSaving(false);
    }
  };
  return (
    <SectionSurface title="HR notes">
      {canEdit ? (
        <div className="space-y-3">
          <Field label="Notes" hint="Internal. The employee does not see these.">
            <Textarea rows={4} maxLength={1000} value={notes} onChange={(event) => { setNotes(event.target.value); setResult(null); }} />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="primary" loading={saving} disabled={!dirty} onClick={save}>Save notes</Button>
            {result ? <span role={result.tone === "danger" ? "alert" : "status"} className={result.tone === "danger" ? "text-body-sm text-danger" : "text-body-sm text-secondary"}>{result.text}</span> : null}
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-body text-foreground">{member.remarks || "No notes."}</p>
      )}
    </SectionSurface>
  );
}

/* ── Page ─────────────────────────────────────────────────────── */

export default function HREmployeeDetail() {
  const auth = useBusinessAuth();
  const { memberId, section = "" } = useParams();
  const [member, setMember] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [payVersion, setPayVersion] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [scheduledSeparation, setScheduledSeparation] = useState(null);
  const [setup, setSetup] = useState({ departments: [], roles: [], employmentTypes: [], hrSettings: { work_days_per_week: 5, work_hours_per_day: 8 } });

  const viewerRole = String(auth?.role || "").toUpperCase();
  const has = useCallback((names) => viewerRole === "OWNER" || names.some((name) => auth?.hasPermission?.(name)), [auth, viewerRole]);
  const can = {
    activity: has(["activity_logs.read.business", "members:read"]),
    compensation: has(["hr:compensation:read", "hr:compensation:manage"]),
    manageCompensation: has(["hr:compensation:manage"]),
    government: has(["employees.read.business", "employees.update.business"]),
    payroll: has(["hr:payroll:read", "hr:payroll:manage"]),
    leave: has(["hr:leave_balances:read", "hr:leave_balances:manage", "hr:leaves:read", "hr:leaves:manage", "hr:leave_requests:read", "hr:leave_requests:review"]),
    editRecord: has(["employees.update.business", "employees.assign.business", "members:invite"]),
    editNotes: has(["employees.update.business", "members:invite"]),
  };

  const loadMember = useCallback(async () => {
    try {
      const res = await businessApi.getMembers();
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setAllMembers(list);
      const found = list.find((m) => String(m.id) === String(memberId)) || null;
      setMember(found);
      setError(found ? "" : "not-found");
    } catch (err) {
      setError(plainError(err, "Check your connection and try again."));
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { setLoading(true); loadMember(); }, [loadMember]);

  useEffect(() => {
    let active = true;
    businessApi.getSeparations({ status: "scheduled" })
      .then((res) => active && setScheduledSeparation((res?.separations || []).find((row) => String(row.memberId) === String(memberId)) || null))
      .catch(() => active && setScheduledSeparation(null));
    return () => { active = false; };
  }, [memberId, member?.status]);

  useEffect(() => {
    let active = true;
    Promise.all([
      businessApi.getDepartments().catch(() => []),
      businessApi.getHRRoles().catch(() => []),
      businessApi.getEmploymentTypes().catch(() => []),
      businessApi.getHRSettings().catch(() => null),
    ]).then(([dept, roles, types, settings]) => {
      if (!active) return;
      const list = (value, key) => (Array.isArray(value?.[key]) ? value[key] : Array.isArray(value?.data) ? value.data : Array.isArray(value) ? value : []);
      setSetup({
        departments: list(dept, "departments").filter((d) => d.status === "active"),
        roles: list(roles, "roles").filter((role) => role.name !== "OWNER"),
        employmentTypes: list(types, "types"),
        hrSettings: settings?.data || settings || { work_days_per_week: 5, work_hours_per_day: 8 },
      });
    });
    return () => { active = false; };
  }, []);

  // Stable per person: the panel reloads whenever its adapter changes.
  const governmentAdapter = useMemo(() => ({
    load: () => businessApi.getEmployeeGovernmentNumbers(memberId),
    reveal: (agency) => businessApi.revealEmployeeGovernmentNumber(memberId, agency),
    save: (changes) => businessApi.updateEmployeeGovernmentNumbers(memberId, changes),
  }), [memberId]);

  const sections = visibleSections(can);
  const base = `/business/hr/employees/${memberId}`;
  const current = sections.find((s) => s.key === section) ? section : "";

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Employee" />
        <Surface><SkeletonRows rows={6} columns={3} label="Loading employee" /></Surface>
      </div>
    );
  }
  if (error || !member) {
    return (
      <div className="space-y-6">
        <PageHeader title="Employee" actions={<Button as={Link} to="/business/hr/employees">Back to employees</Button>} />
        <Surface>
          {error === "not-found" || !member
            ? <EmptyState title="This employee isn't in your business" description="They may have been removed, or the link is for another business." />
            : <ErrorState title="This employee did not load" description={error} onRetry={() => { setLoading(true); loadMember(); }} />}
        </Surface>
      </div>
    );
  }

  const preActivation = isPreActivation(member);
  const former = isFormerMember(member);
  const status = statusWord(member);
  const actions = lifecycleActions({ viewerRole, hasPermission: auth?.hasPermission, member, preActivation, scheduledSeparation });
  const canEditRecord = can.editRecord && !preActivation && !former && !isInvitation(member);
  // A business member with no employment record (typically the owner): shown as that, never as an employee with
  // blanks. Saving an employment record for them is the one deliberate way they become an employee (APP-HR-109).
  const memberOnly = !isEmployeeRecord(member);

  const suspend = async (reason) => {
    await businessApi.suspendMember(member.id, reason || undefined);
    setDialog(null);
    setNotice({ tone: "success", text: `${member.name} is suspended.` });
    await loadMember();
  };
  const reinstate = async () => {
    await businessApi.reinstateMember(member.id);
    setDialog(null);
    setNotice({ tone: "success", text: `${member.name} is reinstated.` });
    await loadMember();
  };
  const saveRecord = async (form) => {
    const enrolling = memberOnly;
    await businessApi.updateMemberProfile(member.id, form);
    setDialog(null);
    // APP-HR-110: enrolling records no pay and no pay schedule on its own; those are the next two deliberate steps.
    setNotice(enrolling
      ? { tone: "success", title: `${member.name} is now an employee`, text: "Next, record their pay, then put them on a pay schedule. Payroll pays them once both are set.", next: true }
      : { tone: "success", text: `${member.name}'s employment record is updated.` });
    await loadMember();
  };

  const headerActions = (
    <>
      {canEditRecord && !memberOnly ? <Button onClick={() => setDialog("edit")}>Edit employment</Button> : null}
      {actions.suspend ? <Button onClick={() => setDialog("suspend")}>Suspend</Button> : null}
      {actions.reinstate ? <Button onClick={() => setDialog("reinstate")}>Reinstate</Button> : null}
      {actions.separate ? <Button as={Link} to={`${base}/separation`}>Separate employee</Button> : null}
    </>
  );

  // Who they are in one glance: the position line, then the employment state as a word with its supporting facts.
  const metaFacts = [
    member.employeeCode,
    member.hireDate ? `Started ${day(String(member.hireDate).slice(0, 10))}` : null,
    member.lastActiveAt ? `Last active ${timeAgo(member.lastActiveAt)}` : null,
  ].filter(Boolean);
  const meta = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <Status tone={status.tone} className="font-medium">{status.label}</Status>
      {metaFacts.map((fact) => <span key={fact}><span aria-hidden="true">· </span>{fact}</span>)}
    </span>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={member.name}
        description={[member.designation || member.position || roleName(member), member.department].filter(Boolean).join(" · ")}
        meta={meta}
        actions={headerActions}
        navigation={(
          <RouteTabs
            label="Employee record"
            items={sections.map((s) => ({ label: s.label, to: s.key ? `${base}/${s.key}` : base, end: !s.key }))}
          />
        )}
      />

      {notice ? (
        <Notice
          tone={notice.tone}
          role="status"
          title={notice.title}
          action={notice.next && can.compensation ? <Button size="sm" as={Link} to={`${base}/pay`}>Go to Pay</Button> : null}
        >
          {notice.text}
        </Notice>
      ) : null}

      {memberStatus(member) === "SUSPENDED" ? (
        <Notice tone="warning" title={`Suspended${member.suspendedAt ? ` since ${day(member.suspendedAt)}` : ""}`}>
          {member.suspensionReason ? `Reason: ${member.suspensionReason}. ` : ""}They cannot sign in to the business, and payroll calculated while they are suspended leaves them out.
        </Notice>
      ) : null}
      {scheduledSeparation && !former ? (
        <Notice
          tone="warning"
          title={`Leaving: last working day ${formatDay(scheduledSeparation.lastWorkingDate, { year: true })}`}
          action={<Button size="sm" as={Link} to={`${base}/separation`}>Open separation</Button>}
        >
          They stay active until then. The separation completes after that day once the offboarding checklist is clear.
        </Notice>
      ) : null}
      {former ? (
        <Notice
          title={memberStatus(member) === "LEFT" ? "Left before starting work" : `Former employee${member.lastWorkingDate ? ` · last working day ${formatDay(member.lastWorkingDate, { year: true })}` : ""}`}
          action={<Button size="sm" as={Link} to={`${base}/separation`}>{memberStatus(member) === "LEFT" ? "How they left" : "Separation record"}</Button>}
        >
          They no longer work here. Their HR records stay available.
        </Notice>
      ) : null}
      {memberOnly ? (
        <Notice
          title={isOwnerMember(member) ? "Business Owner — payroll not configured" : "No employment record yet"}
          action={canEditRecord ? <Button size="sm" onClick={() => setDialog("edit")}>Add employment record</Button> : null}
        >
          {isOwnerMember(member)
            ? `${member.name} owns and works in this business. No employment record is kept for them, so payroll does not pay them a salary. Add one, with a start date, only if the business pays them through payroll.`
            : `${member.name} works in the business${roleName(member) ? ` as ${roleName(member)}` : ""}, but no employment record is kept for them yet: no start date, employment type or employee code. Add one so payroll can pay them.`}
        </Notice>
      ) : null}
      {preActivation && !former ? (
        <Notice
          title="Still in the old invitation onboarding"
          action={actions.endOnboarding ? <Button size="sm" as={Link} to={`${base}/separation`}>End onboarding</Button> : null}
        >
          Invitation onboarding was replaced by Recruitment. End this onboarding, then hire them through Recruitment › Direct hire.
          {member.onboardingMissingCount > 0 ? ` Still missing: ${member.onboardingMissingItems.map(words).join(", ")}.` : ""}
        </Notice>
      ) : null}

      {current === "" ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-6">
            <SectionSurface
              title="Employment"
              action={canEditRecord && !memberOnly ? <Button size="sm" variant="quiet" onClick={() => setDialog("edit")}>Edit</Button> : null}
            >
              {/* The job first, then what they may do in the business: two different things (a position is not an access role). */}
              <div className="space-y-5">
                {memberOnly ? (
                  <div className="space-y-2">
                    <h3 className="text-label text-secondary">Job</h3>
                    <p className="text-body-sm text-tertiary">No employment record.</p>
                  </div>
                ) : null}
                {memberOnly ? null : <FactGroup
                  title="Job"
                  items={[
                    ["Position", member.designation || member.position],
                    ["Department", member.department],
                    ["Supervisor", member.supervisorName],
                    ["Employment type", member.employmentTypeLabel],
                    ["Start date", member.hireDate ? day(String(member.hireDate).slice(0, 10)) : null],
                    ["Employee code", member.employeeCode],
                  ]}
                />}
                <div className="border-t border-subtle pt-4">
                  <FactGroup
                    title="Access to the business"
                    items={[
                      ["Role", roleName(member)],
                      ["Authority", member.hierarchyLabel || authorityLabel(member.hierarchyLevel)],
                      ["Status", status.label],
                    ]}
                    hint="The role decides what they can open and do in the business portal."
                  />
                </div>
              </div>
            </SectionSurface>
            <SectionSurface title="Contact and personal details">
              <div className="space-y-5">
                <FactGroup
                  title="Contact"
                  items={[
                    ["Work email", member.employmentEmail || member.email, { wide: true }],
                    ["Contact number", member.contactNumber],
                    ["Account email", member.email, { wide: true }],
                  ]}
                />
                <div className="border-t border-subtle pt-4">
                  <FactGroup
                    title="Personal details"
                    none="No personal details are recorded."
                    items={[
                      ["Birth date", member.birthDate ? day(member.birthDate) : null],
                      ["Gender", member.gender ? words(member.gender) : null],
                      ["Civil status", member.civilStatus ? words(member.civilStatus) : null],
                      ["Address", [member.addressLine1, member.addressLine2].filter(Boolean).join(", ")],
                      ["City or municipality", member.cityMunicipality],
                      ["Province", [member.province, member.postalCode].filter(Boolean).join(" ")],
                    ]}
                  />
                </div>
              </div>
            </SectionSurface>
          </div>
          <div className="min-w-0 space-y-6">
            <SectionSurface title="Emergency contact">
              <FactGroup
                columns={1}
                none="No emergency contact is recorded."
                items={[
                  ["Name", member.emergencyContactName],
                  ["Relationship", member.emergencyContactRelationship],
                  ["Contact number", member.emergencyContactNumber],
                  ...(member.emergencyContactNotes ? [["Notes", member.emergencyContactNotes]] : []),
                ]}
              />
            </SectionSurface>
            {!isInvitation(member) ? <NotesSurface member={member} canEdit={can.editNotes && !former} onSaved={loadMember} /> : null}
          </div>
        </div>
      ) : null}

      {current === "attendance" ? <AttendanceSection member={member} /> : null}
      {current === "leave" ? <LeaveSection member={member} /> : null}
      {current === "pay" ? (
        <div className="space-y-6">
          {memberOnly ? (
            <Notice
              title="Pay is recorded for employees"
              action={canEditRecord ? <Button size="sm" onClick={() => setDialog("edit")}>Add employment record</Button> : null}
            >
              {member.name} is a business member with no employment record, so no pay can be recorded and no payroll pays them.
              Add an employment record with a start date first.
            </Notice>
          ) : null}
          {can.compensation && !memberOnly ? (
            <SectionSurface title="Pay">
              <EmployeeCompensationPanel
                memberId={member.id}
                memberName={member.name}
                canManage={can.manageCompensation && !former}
                isPreActivation={preActivation}
                showPreActivationNote={preActivation}
                employmentTypes={setup.employmentTypes.length ? setup.employmentTypes : [{ value: member.employmentType || "full_time", label: words(member.employmentType || "full_time") }]}
                defaultEmploymentType={member.employmentType || "full_time"}
                hireDate={member.hireDate || null}
                hrSettings={setup.hrSettings}
                onChanged={async () => { setPayVersion((version) => version + 1); await loadMember(); }}
                showToast={(tone, text) => setNotice({ tone: tone === "error" ? "danger" : tone, text })}
              />
            </SectionSurface>
          ) : null}
          {can.payroll && !isInvitation(member) && !memberOnly ? (
            <SectionSurface title="Pay schedule">
              <EmployeePaySchedulePanel
                memberId={member.id}
                memberName={member.name}
                former={former}
                refreshKey={payVersion}
                showToast={(tone, text) => setNotice({ tone: tone === "error" ? "danger" : tone, text })}
              />
            </SectionSurface>
          ) : null}
          {(can.payroll || can.compensation) && !isInvitation(member) && !memberOnly ? <PayRuleApplicabilityPanel memberId={member.id} memberName={member.name} refreshKey={payVersion} /> : null}
          {can.payroll && !isInvitation(member) && !memberOnly ? <HolidayPayHandlingPanel memberId={member.id} memberName={member.name} /> : null}
          {can.compensation && !isInvitation(member) && !memberOnly ? <PayRuleCoveragePanel memberId={member.id} memberName={member.name} /> : null}
        </div>
      ) : null}
      {current === "government" ? (
        <div className="space-y-6">
          {can.government ? (
            <GovernmentNumbersPanel
              adapter={governmentAdapter}
              labels={EMPLOYEE_NUMBER_LABELS}
              subtitle="Recorded means the number is on file here, not that the agency has confirmed it."
              readOnlyNote="You can see which numbers are on file. Showing or changing a number needs an HR manager or the owner."
            />
          ) : null}
          {can.compensation ? <StatutoryFactsPanel memberId={member.id} /> : null}
        </div>
      ) : null}
      {current === "payslips" ? <PayslipsSection member={member} /> : null}
      {current === "deductions" ? <DeductionsSection member={member} /> : null}
      {current === "financials" ? <FinancialsSection member={member} /> : null}
      {current === "history" ? <HistorySection member={member} /> : null}
      {current === "activity" ? <ActivitySection member={member} /> : null}

      {dialog === "edit" ? (
        <EmploymentStructureModal
          open
          member={member}
          departments={setup.departments}
          roles={setup.roles}
          members={allMembers}
          employmentTypes={setup.employmentTypes}
          canChangeRole={viewerRole === "OWNER"}
          adding={memberOnly}
          onClose={() => setDialog(null)}
          onSubmit={saveRecord}
        />
      ) : null}
      {dialog === "suspend" ? <SuspendDialog member={member} onConfirm={suspend} onClose={() => setDialog(null)} /> : null}
      {dialog === "reinstate" ? (
        <ReinstateDialog
          member={member}
          returnsToOnboarding={PRE_ACTIVATION_STAGES.has(String(member.onboardingStage || "").toUpperCase())}
          onConfirm={reinstate}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
